import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { FOOTER } from "@/constants/testIds";

const EASE = [0.76, 0, 0.24, 1];

// KingTide-inspired lagged wordmark:
// HANKDAVID drops from above as the user scrolls into the footer,
// with ~0.5s spring lag trailing the scroll cursor.
function KingTideWordmark() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end end"],
  });
  // Spring smooths the scroll signal so the wordmark visibly lags the scroll input.
  // ~0.5s to settle: stiffness ~50, damping ~18.
  const lagged = useSpring(scrollYProgress, {
    stiffness: 50,
    damping: 18,
    mass: 1,
  });
  const y = useTransform(lagged, [0, 1], ["-45%", "0%"]);
  const opacity = useTransform(lagged, [0, 0.4, 1], [0, 0.5, 1]);

  return (
    <div
      ref={ref}
      className="relative -mx-6 md:-mx-10 overflow-hidden select-none"
      style={{ height: "clamp(180px, 22vw, 340px)" }}
    >
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-x-0 bottom-0 font-display leading-[0.82] tracking-[-0.06em] whitespace-nowrap text-center text-[calc(100vw/5)] text-white/[0.12]"
      >
        HANKDAVID
      </motion.div>
    </div>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      id="footer"
      data-testid={FOOTER.root}
      className="relative bg-[#0a0a0b] text-white border-t border-white/[0.06]"
    >
      <div className="px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-10 mb-16">
          <div>
            <div className="mono-label">Locations</div>
            <div className="mt-4 space-y-1 text-white/70 text-sm leading-relaxed">
              <div>HANKDAVID</div>
              <div className="whitespace-nowrap">Detroit &nbsp;|&nbsp; Miami &nbsp;|&nbsp; Portland</div>
            </div>
          </div>
          <div>
            <div className="mono-label">Contact</div>
            <div className="mt-4 space-y-2 text-white/70 text-sm">
              <a className="hd-link !block w-fit" data-cursor="MAIL" href="mailto:info@hankdavid.com">info@hankdavid.com</a>
              <a className="hd-link !block w-fit" data-cursor="→" href="#contact">Begin a project →</a>
            </div>
          </div>
          <div>
            <div className="mono-label">Elsewhere</div>
            <div className="mt-4 space-y-2 text-white/70 text-sm">
              <a className="hd-link block" data-cursor="↗" href="https://instagram.com/hankdavidco" target="_blank" rel="noreferrer">@hankdavidco {"\u2197\uFE0E"}</a>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: EASE }}
          style={{ transformOrigin: "left" }}
          className="hairline"
        />

        <div className="mt-10 md:mt-14">
          <KingTideWordmark />
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-center md:items-center justify-between gap-4 text-center md:text-left">
          <div className="mono-label">© {year} HANKDAVID</div>
          <div className="mono-label">Building worlds for the discerning</div>
          <div className="mono-label">HQ · Detroit</div>
        </div>
      </div>
    </footer>
  );
}
